import {
	type Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react';

import { useValueChangeEffect } from 'hooks';
import type { YouTubePlayer } from 'models/vendor';
import { cnm } from 'utils/classNames';

import { getVideoEmbedUrl, getVideoId } from './helpers';
import YouTubeAPILoader from './YouTubeAPILoader';

/** Methods will only be set if the player is ready. */
export interface VideoEmbedAPI {
	pause?: () => void;
	play?: () => void;
}

type SourceProps =
	| {
			/** Base video URL, the correct embed format will be handled automatically. */
			url: string;
			youTubeId?: never;
	  }
	| { url?: never; youTubeId: string };

type ApiProps =
	| {
			/**
			 * Load the YouTube iframe API.
			 *
			 * @see https://developers.google.com/youtube/iframe_api_reference
			 */
			hasApi: true;
			/** When the player is ready to be controlled. */
			onReady?: (video: Required<VideoEmbedAPI>) => void;
			/** Ref for controlling the player. */
			ref: Ref<VideoEmbedAPI>;
	  }
	| { hasApi?: false; onReady?: never; ref?: never };

type Props = SourceProps &
	ApiProps & {
		className?: string;
		/** Descriptive title for accessibility. */
		iframeTitle?: string;
	};

/**
 * A responsive, embedded video player.
 *
 * Handles loading the YouTube iframe API and exposing parts of a player
 * instance via ref, if the `hasApi` prop is true.
 *
 * Typical reactive patterns like useLazyGlobalValue don't work well with the
 * API since it's asynchronously loaded and initialized in parts, and ready
 * state is communicated via callbacks instead of promises or events.
 * Before the API itself calls its global ready callback, the YT object can be
 * available while YT.Player is undefined. Same with constructing the player
 * instance, the object can be ready while methods like playVideo are undefined.
 * Use callback props to know when things are fully ready before using them.
 */
export default function VideoEmbed({
	className,
	hasApi = false,
	iframeTitle,
	onReady,
	ref,
	url,
	youTubeId,
}: Props) {
	const videoId = url ? getVideoId(url) : youTubeId;
	const [isApiReady, setIsApiReady] = useState(false);

	// The loading flow when the API is used:
	// 1. Set embed URL on client mount to render the API loader and iframe.
	// 2. Set API ready state to true when the API is loaded.
	// 3. The iframe ref callback constructs a player instance when the iframe
	//    node and API are ready.
	// 4. The player instance is set to state when it's ready.
	// 5. The public API gets methods when the player instance is available.
	// 6. The onReady prop is called when the public API is available.

	// Enabling the iframe API requires setting origin which isn't available
	// on the server.
	const [embedUrl, setEmbedUrl] = useState<string | undefined>(
		hasApi ? undefined : getVideoEmbedUrl(videoId),
	);
	useEffect(() => {
		if (hasApi) {
			setEmbedUrl(
				getVideoEmbedUrl(videoId, {
					hasAPI: true,
					origin: globalThis.location.origin,
				}),
			);
		}
	}, [hasApi, videoId]);

	const [player, setPlayer] = useState<YouTubePlayer | undefined>(undefined);
	const iframeRef = useCallback(
		(node: HTMLIFrameElement | null) => {
			if (!isApiReady || !node || !globalThis.YT?.Player) {
				return;
			}
			let isActive = true;
			// The player is constructed immediately but does some kind of async
			// initialization, set to state when ready and if it's still the same
			// component instance (the isActive check).
			const p = new globalThis.YT.Player(node, {
				events: {
					onReady: () => {
						if (isActive) {
							setPlayer(p);
						}
					},
				},
			});

			// No player.destroy() in cleanup, that removes DOM nodes.
			return () => {
				isActive = false;
				setPlayer(undefined);
			};
		},
		[isApiReady],
	);

	const publicApi = useMemo(() => {
		if (!player) {
			return {};
		}
		return {
			pause: () => {
				player.pauseVideo();
			},
			play: () => {
				player.playVideo();
			},
		};
	}, [player]);

	// Expose player controls via custom ref.
	useImperativeHandle(ref, () => publicApi, [publicApi]);

	useValueChangeEffect(publicApi, (prevPublicApi) => {
		if (publicApi.play && !prevPublicApi.play && onReady) {
			onReady(publicApi);
		}
	});

	if (!embedUrl) {
		return null;
	}

	return (
		<>
			{hasApi && <YouTubeAPILoader onReady={() => setIsApiReady(true)} />}
			<iframe
				ref={iframeRef}
				className={cnm('aspect-video w-full', className)}
				src={embedUrl}
				title={iframeTitle}
				// Cloudflare settings can mess with the referrer policy
				// so force it to what YouTube wants.
				// https://developers.google.com/youtube/terms/required-minimum-functionality#embedded-player-api-client-identity
				referrerPolicy="strict-origin-when-cross-origin"
				// If the YouTube API would create the iframe, it would set everything
				// below except fullscreen. One of the others (picture-in-picture
				// possibly) seems to implicitly also allow fullscreen, but add it
				// anyway just to be safe and maybe more future-proof.
				// https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Permissions-Policy#directives
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
			/>
		</>
	);
}
VideoEmbed.displayName = 'VideoEmbed';
